import {
  addDateOnlyMonths,
  createCalendarGrid,
  formatDateOnly,
  formatDateOnlyLabel,
  moveCalendarDate,
  parseDateOnly
} from "../core/date-navigation.js";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export class AlertExpirationPicker {
  constructor(element) {
    this.element = element;
    this.input = required("[name='expiration']", element);
    this.toggle = required("[data-alert-expiration-toggle]", element);
    this.label = required("[data-alert-expiration-label]", element);
    this.popover = required("[data-alert-expiration-popover]", element);
    this.heading = required("[data-alert-expiration-heading]", element);
    this.calendar = required("[data-alert-expiration-calendar]", element);
    this.time = required("[data-alert-expiration-time]", element);
    this.previous = required("[data-alert-expiration-previous]", element);
    this.next = required("[data-alert-expiration-next]", element);
    this.clear = required("[data-alert-expiration-clear]", element);
    this.done = required("[data-alert-expiration-done]", element);
    this.abortController = new AbortController();
    this.selectedDate = null;
    this.focusedDate = todayDate();
    this.displayDate = { ...this.focusedDate };

    const options = { signal: this.abortController.signal };
    this.toggle.addEventListener("click", () => this.togglePopover(), options);
    this.previous.addEventListener("click", () => this.changeMonth(-1), options);
    this.next.addEventListener("click", () => this.changeMonth(1), options);
    this.calendar.addEventListener("click", (event) => this.selectDay(event), options);
    this.calendar.addEventListener("keydown", (event) => this.handleCalendarKeydown(event), options);
    this.time.addEventListener("change", () => this.syncValue(), options);
    this.clear.addEventListener("click", () => this.setValue(""), options);
    this.done.addEventListener("click", () => this.close(), options);
    document.addEventListener("pointerdown", (event) => {
      if (!this.popover.hidden && !this.element.contains(event.target)) this.close();
    }, options);
    this.element.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || this.popover.hidden) return;
      event.preventDefault();
      this.close();
      this.toggle.focus({ preventScroll: true });
    }, options);
    this.setValue("");
  }

  dispose() {
    this.abortController.abort();
  }

  setValue(value) {
    const parsed = parseLocalDateTime(value);
    this.selectedDate = parsed?.date || null;
    this.focusedDate = parsed?.date || todayDate();
    this.displayDate = { ...this.focusedDate };
    this.time.value = parsed?.time || "16:00";
    this.syncValue();
    this.render();
    if (!value) this.close();
  }

  togglePopover() {
    if (this.popover.hidden) this.open();
    else this.close();
  }

  open() {
    this.popover.hidden = false;
    this.toggle.setAttribute("aria-expanded", "true");
    this.render({ focus: true });
  }

  close() {
    this.popover.hidden = true;
    this.toggle.setAttribute("aria-expanded", "false");
  }

  changeMonth(amount) {
    this.focusedDate = addDateOnlyMonths(this.focusedDate, amount);
    this.displayDate = { ...this.focusedDate };
    this.render({ focus: true });
  }

  selectDay(event) {
    const button = event.target instanceof Element
      ? event.target.closest("button[data-date]")
      : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const date = parseDateOnly(button.dataset.date);
    if (!date) return;
    this.selectedDate = date;
    this.focusedDate = date;
    this.displayDate = { ...date };
    this.syncValue();
    this.render({ focus: true });
  }

  handleCalendarKeydown(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectDay(event);
      return;
    }
    const moved = moveCalendarDate(this.focusedDate, event.key, event);
    if (!moved) return;
    event.preventDefault();
    this.focusedDate = moved;
    this.displayDate = { ...moved };
    this.render({ focus: true });
  }

  render({ focus = false } = {}) {
    this.heading.textContent = `${MONTH_NAMES[this.displayDate.month - 1]} ${
      this.displayDate.year
    }`;
    const today = todayDate();
    const focusedValue = formatDateOnly(this.focusedDate);
    const cells = createCalendarGrid(this.displayDate, this.selectedDate, today);
    this.calendar.replaceChildren(...cells.map((cell) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "alert-expiration-day";
      button.dataset.date = cell.value;
      button.textContent = String(cell.date.day);
      button.tabIndex = cell.value === focusedValue ? 0 : -1;
      button.classList.toggle("is-overflow", !cell.inMonth);
      button.classList.toggle("is-selected", cell.selected);
      button.classList.toggle("is-today", cell.today);
      button.setAttribute("aria-label", formatDateOnlyLabel(cell.date));
      button.setAttribute("aria-selected", String(cell.selected));
      if (cell.today) button.setAttribute("aria-current", "date");
      return button;
    }));
    if (focus) {
      requestAnimationFrame(() => this.calendar.querySelector(
        `[data-date="${focusedValue}"]`
      )?.focus({ preventScroll: true }));
    }
  }

  syncValue() {
    if (!this.selectedDate) {
      this.input.value = "";
      this.label.textContent = "No expiration";
      this.clear.disabled = true;
      return;
    }
    const time = /^\d{2}:\d{2}$/.test(this.time.value) ? this.time.value : "16:00";
    this.input.value = `${formatDateOnly(this.selectedDate)}T${time}`;
    this.label.textContent = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(new Date(
      this.selectedDate.year,
      this.selectedDate.month - 1,
      this.selectedDate.day,
      Number(time.slice(0, 2)),
      Number(time.slice(3, 5))
    ));
    this.clear.disabled = false;
  }
}

function parseLocalDateTime(value) {
  const match = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/
  );
  if (!match) return null;
  const date = parseDateOnly(`${match[1]}-${match[2]}-${match[3]}`);
  return date ? { date, time: `${match[4] || "16"}:${match[5] || "00"}` } : null;
}

function todayDate() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate()
  };
}

function required(selector, root) {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Required alert expiration element not found: ${selector}`);
  }
  return element;
}

